import React, { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, Sun, Moon, Github, Twitter, Linkedin } from 'lucide-react';
import { useTheme } from '../context/ThemeContext';
import { Search } from './Search';
import { NavDropdown } from './NavDropdown';

export function Layout({ children }: { children: React.ReactNode }) {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const { theme, toggleTheme } = useTheme();
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 10);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const navItems = [
    { path: '/research', label: 'Research' },
    { path: '/company', label: 'Company' },
    { path: '/careers', label: 'Careers' },
    { path: '/safety', label: 'Safety & Ethics' },
    { path: '/blog', label: 'Blog' }
  ];

  const modelDropdownItems = [
    { 
      path: '/models/fractal', 
      label: 'FRACTAL 3B', 
      description: 'Protein structure predictor' 
    }
  ];

  const projectDropdownItems = [
    { 
      path: '/projects/huxley', 
      label: 'Huxley', 
      description: 'Biological Computational Engine' 
    }
  ];

  const isActive = (path: string) => location.pathname === path;

  return (
    <div className={`min-h-screen ${
      theme === 'dark' 
        ? 'bg-dark-100 text-white' 
        : 'bg-gray-50 text-gray-900'
    }`}>
      {/* Navigation */}
      <nav className={`fixed top-0 w-full z-50 transition-all duration-300 ${
        isScrolled
          ? theme === 'dark'
            ? 'bg-dark-200/80 backdrop-blur-lg border-dark-300'
            : 'bg-white/80 backdrop-blur-lg border-gray-200'
          : 'bg-transparent'
        } ${isScrolled ? 'border-b' : ''}`}
      >
        <div className="max-w-[1440px] mx-auto px-6">
          <div className="flex items-center justify-between h-20">
            <div className="flex items-center space-x-12">
              <Link to="/" className="group">
                <span className="text-xl font-semibold transition-all duration-300 group-hover:opacity-80">
                  Huxley Research
                </span>
              </Link>
              <div className="hidden lg:flex space-x-8">
                {navItems.map((item) => (
                  <Link
                    key={item.path}
                    to={item.path}
                    className={`relative text-sm font-medium px-4 py-2 rounded-full transition-colors duration-300 ${
                      isActive(item.path)
                        ? theme === 'dark'
                          ? 'text-white bg-dark-300'
                          : 'text-primary-600 bg-primary-50'
                        : theme === 'dark'
                          ? 'text-gray-300 hover:text-white hover:bg-dark-300'
                          : 'text-gray-600 hover:text-primary-600 hover:bg-primary-50'
                    }`}
                  >
                    {item.label}
                  </Link>
                ))}
                <NavDropdown 
                  label="Models" 
                  items={modelDropdownItems}
                  isActive={location.pathname.startsWith('/models')}
                />
                <NavDropdown 
                  label="Projects" 
                  items={projectDropdownItems}
                  isActive={location.pathname.startsWith('/projects')}
                />
              </div>
            </div>
            <div className="flex items-center space-x-4">
              <Search />
              <button
                onClick={toggleTheme}
                className={`p-2 rounded-full transition-colors duration-300 ${
                  theme === 'dark'
                    ? 'text-gray-400 hover:text-white hover:bg-dark-300'
                    : 'text-gray-600 hover:text-primary-600 hover:bg-primary-50'
                }`}
              >
                {theme === 'dark' ? <Sun className="w-5 h-5" /> : <Moon className="w-5 h-5" />}
              </button>
              <button 
                className={`lg:hidden p-2 rounded-full transition-colors duration-300 ${
                  theme === 'dark'
                    ? 'text-gray-400 hover:text-white hover:bg-dark-300'
                    : 'text-gray-600 hover:text-primary-600 hover:bg-primary-50'
                }`}
                onClick={() => setIsMenuOpen(!isMenuOpen)}
              >
                {isMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
              </button>
            </div>
          </div>
        </div>
      </nav>

      {/* Mobile Menu */}
      <div className={`fixed inset-0 z-40 lg:hidden transition-all duration-300 ${
        isMenuOpen ? 'opacity-100 pointer-events-auto' : 'opacity-0 pointer-events-none'
      }`}>
        <div className="absolute inset-0 bg-black/20 backdrop-blur-sm" onClick={() => setIsMenuOpen(false)} />
        <div className={`absolute right-0 top-0 h-full w-64 transform transition-transform duration-300 ${
          isMenuOpen ? 'translate-x-0' : 'translate-x-full'
        } ${theme === 'dark' ? 'bg-dark-200' : 'bg-white'}`}>
          <div className="p-6 pt-20">
            {navItems.map((item) => (
              <Link
                key={item.path}
                to={item.path}
                className={`block py-3 px-4 rounded-lg text-lg font-medium transition-colors duration-300 ${
                  isActive(item.path)
                    ? theme === 'dark'
                      ? 'bg-dark-300 text-white'
                      : 'bg-primary-50 text-primary-600'
                    : theme === 'dark'
                      ? 'text-gray-300 hover:bg-dark-300 hover:text-white'
                      : 'text-gray-600 hover:bg-primary-50 hover:text-primary-600'
                }`}
                onClick={() => setIsMenuOpen(false)}
              >
                {item.label}
              </Link>
            ))}
            <div className="mt-4 border-t border-gray-200 dark:border-dark-300 pt-4">
              <div className={`text-sm font-semibold mb-2 px-4 ${
                theme === 'dark' ? 'text-gray-400' : 'text-gray-500'
              }`}>
                Models
              </div>
              {modelDropdownItems.map((item) => (
                <Link
                  key={item.path}
                  to={item.path}
                  className={`block py-2 px-4 ml-4 rounded-lg text-base font-medium transition-colors duration-300 ${
                    isActive(item.path)
                      ? theme === 'dark'
                        ? 'bg-dark-300 text-white'
                        : 'bg-primary-50 text-primary-600'
                      : theme === 'dark'
                        ? 'text-gray-300 hover:bg-dark-300 hover:text-white'
                        : 'text-gray-600 hover:bg-primary-50 hover:text-primary-600'
                  }`}
                  onClick={() => setIsMenuOpen(false)}
                >
                  {item.label}
                </Link>
              ))}
            </div>
            <div className="mt-4 border-t border-gray-200 dark:border-dark-300 pt-4">
              <div className={`text-sm font-semibold mb-2 px-4 ${
                theme === 'dark' ? 'text-gray-400' : 'text-gray-500'
              }`}>
                Projects
              </div>
              {projectDropdownItems.map((item) => (
                <Link
                  key={item.path}
                  to={item.path}
                  className={`block py-2 px-4 ml-4 rounded-lg text-base font-medium transition-colors duration-300 ${
                    isActive(item.path)
                      ? theme === 'dark'
                        ? 'bg-dark-300 text-white'
                        : 'bg-primary-50 text-primary-600'
                      : theme === 'dark'
                        ? 'text-gray-300 hover:bg-dark-300 hover:text-white'
                        : 'text-gray-600 hover:bg-primary-50 hover:text-primary-600'
                  }`}
                  onClick={() => setIsMenuOpen(false)}
                >
                  {item.label}
                </Link>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <main className="pt-20">
        {children}
      </main>

      {/* Footer */}
      <footer className={`py-20 ${
        theme === 'dark' 
          ? 'bg-dark-200 border-dark-300' 
          : 'bg-white border-gray-200'
      } border-t`}>
        <div className="max-w-[1440px] mx-auto px-6">
          <div className="grid md:grid-cols-4 gap-12 mb-16">
            <div>
              <div className="text-xl font-medium mb-8">Huxley Research</div>
              <div className="flex space-x-6">
                {[
                  { Icon: Twitter, href: '#' },
                  { Icon: Linkedin, href: '#' },
                  { Icon: Github, href: '#' }
                ].map(({ Icon, href }, index) => (
                  <a
                    key={index}
                    href={href}
                    className={`transform hover:scale-110 transition-all duration-300 ${
                      theme === 'dark'
                        ? 'text-gray-400 hover:text-white'
                        : 'text-gray-500 hover:text-primary-600'
                    }`}
                  >
                    <Icon className="w-5 h-5" />
                  </a>
                ))}
              </div>
            </div>
            {[
              {
                title: 'Research',
                links: [
                  { name: 'Publications', href: '/research' },
                  { name: 'Blog', href: '/blog' },
                  { name: 'Open source', href: '/research' }
                ]
              },
              {
                title: 'Company',
                links: [
                  { name: 'About us', href: '/about' },
                  { name: 'Careers', href: '/careers' }
                ]
              },
              {
                title: 'Resources',
                links: [
                  { name: 'Documentation', href: '/documentation' },
                  { name: 'Privacy', href: '/privacy' },
                  { name: 'Terms', href: '/terms' }
                ]
              }
            ].map((section, index) => (
              <div key={index}>
                <h4 className="text-sm font-semibold mb-6">{section.title}</h4>
                <ul className="space-y-4">
                  {section.links.map((link, linkIndex) => (
                    <li key={linkIndex}>
                      <Link 
                        to={link.href}
                        className={`text-sm transition-colors duration-300 ${
                          theme === 'dark'
                            ? 'text-gray-400 hover:text-white'
                            : 'text-gray-600 hover:text-primary-600'
                        }`}
                      >
                        {link.name}
                      </Link>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
          <div className={`text-sm ${
            theme === 'dark' ? 'text-gray-400' : 'text-gray-500'
          }`}>
            <p>&copy; {new Date().getFullYear()} Huxley Research. All rights reserved.</p>
          </div>
        </div>
      </footer>
    </div>
  );
}